import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, FormControl, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import dayjs from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { getAllClaims, getMyClaims } from '../../api/incentiveClaimApi';
import { getAllIncentiveSlips, getIncentiveSlipsByAgentId } from '../../api/incentiveSlipApi';
import { useUser } from '../../context/userContext';
import { getAllCampaigns, getCampaignProgress, getCampaignLeaderboard } from '../../api/incentiveApi';
import { handlePriceWithRM } from '../../utils/priceUtils';

const FONT = "'Poppins', sans-serif";

/* ============================================================================
   INCENTIVE REPORT  ·  two pages off one component
     /reports/incentive       My Incentive Report   — the agent's own incentive
     /reports/incentive/all   All Incentive Report  — Accountant / Super Admin only,
                                                      route-guarded

   Same split as the commission reports, for the same reason: an agent must never
   see what anybody else earned, so the company-wide view lives behind its own
   route guard rather than a hidden tab on a page they can load. The My page also
   asks the API for its own data (GetMine / slips-by-agent), so even a broken route
   can't hand it someone else's rows. (user 2026-08-27)

   Kept separate from the Commission Report on purpose. Incentive is per-agent
   PER CAMPAIGN, and case incentives are banded on an agent's total rather than
   attributable to one sale — so they cannot sit as columns beside per-sale
   commission rows without either hiding which campaign paid, or exploding the
   table three columns per campaign.

   THE TWO SIDES, and which is the truth for what (user 2026-08-27):
     claims  →  what WEBBEY claims from Astern
     slips   →  what is PAID TO THE AGENT
   So EARNED comes from claims and PAID comes from slips. They are different
   things, not two versions of one number, and the gap between them is real
   money owed rather than a discrepancy to reconcile away.

   Trip rewards (Revenue-metric campaigns) carry no cash amount — they are
   tiers, not ringgit — so they are counted separately and never folded into a
   cash total.
   ========================================================================= */

const StatCard = ({ label, value, sub, borderColor }) => (
  <Card sx={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '14px' }}>
    <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

// allScope=true renders the company-wide report (Accountant / Super Admin only,
// route-guarded); default is the agent's own incentive.
export default function IncentiveReport({ allScope = false }) {
  const { user } = useUser();
  const myId = user?.userId;
  const [claims, setClaims] = useState([]);
  const [slips, setSlips] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // The My page never calls the company-wide endpoints. Both routes render this
      // same component at the same position in the tree, so React reuses the instance
      // when you navigate between them — deriving the scope from the prop rather than
      // from state is what stops the previous page's data being shown here.
      const claimsCall = allScope ? getAllClaims() : getMyClaims();
      const slipsCall = allScope
        ? getAllIncentiveSlips()
        : (myId ? getIncentiveSlipsByAgentId(myId) : Promise.resolve({ data: [] }));
      const [c, s, cam] = await Promise.all([
        claimsCall.catch(() => ({ data: [] })),
        slipsCall.catch(() => ({ data: [] })),
        getAllCampaigns().catch(() => ({ data: [] })),
      ]);
      setClaims(c.data || []);
      setSlips(s.data || []);
      setCampaigns(cam.data || []);
    } finally {
      setLoading(false);
    }
  }, [allScope, myId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // EARNED — the agent's own Incentive rows, never Void. "Total Incentive" and
  // "Webbey Incentive" rows are the Astern/company side and are not agent earnings.
  const earnedRows = useMemo(() => claims.filter(r =>
    r.rowType === 'Incentive'
    && r.status !== 'Void'
    && (campaignFilter === 'all' || String(r.campaignId) === String(campaignFilter))
  ), [claims, campaignFilter]);

  // PAID — approved slips only. A slip that is Pending or Voided has not paid anybody.
  const paidSlips = useMemo(() => slips.filter(s =>
    s.status === 'Approved'
    && (campaignFilter === 'all' || String(s.campaignId) === String(campaignFilter))
  ), [slips, campaignFilter]);

  const cashEarned = useMemo(() => earnedRows.reduce((sum, r) => sum + (r.amount ?? 0), 0), [earnedRows]);
  const totalPaid = useMemo(() => paidSlips.reduce((sum, s) => sum + (s.totalPayout ?? 0), 0), [paidSlips]);
  const pending = Math.max(0, cashEarned - totalPaid);

  // Trip / tier rewards carry no ringgit value — surfaced as a count so they are
  // visible without contaminating the cash figures. Counted as distinct AWARDS
  // (one agent, one campaign), not rows: a trip is earned once per campaign no
  // matter how many sales fed the tier, and a raw row count read as 600 trips.
  const tripAwards = useMemo(() => {
    const set = new Set();
    earnedRows.forEach(r => { if (r.amount == null) set.add(`${r.campaignId}|${r.beneficiaryAgentId}`); });
    return set.size;
  }, [earnedRows]);

  const activeCampaigns = useMemo(() => campaigns.filter(c => {
    const now = dayjs();
    return c.status === 'Active' && (!c.endDate || dayjs(c.endDate).isAfter(now));
  }).length, [campaigns]);

  const campaignById = useMemo(() => {
    const m = {};
    campaigns.forEach(c => { m[c.campaignId] = c; });
    return m;
  }, [campaigns]);

  /* ---------- By campaign ----------
     `units` is the achievement in the campaign's own terms, from Contribution:
     battery units on an Item campaign, cases on a Case campaign. It is what the
     ringgit is calculated FROM, and on an Item campaign it's the number people
     actually talk about ("she sold 12 batteries"), so it earns its own column.
     Co-broke arrives already halved, hence the decimals. */
  const byCampaign = useMemo(() => {
    const map = {};
    const touch = (id, name) => (map[id] ||= {
      campaignId: id, name: name || `Campaign ${id}`,
      earned: 0, paid: 0, units: 0, agents: new Set(), metric: '',
    });
    earnedRows.forEach(r => {
      const m = touch(r.campaignId, r.campaignName);
      m.earned += r.amount ?? 0;
      m.units += r.contribution ?? 0;
      m.metric = r.metric || m.metric;
      if (r.beneficiaryAgentId) m.agents.add(r.beneficiaryAgentId);
    });
    paidSlips.forEach(s => { touch(s.campaignId, s.campaignName).paid += s.totalPayout ?? 0; });
    return Object.values(map)
      .map(c => ({
        ...c,
        agents: c.agents.size,
        pending: Math.max(0, c.earned - c.paid),
        type: campaignById[c.campaignId]?.type || '',
      }))
      .sort((a, b) => b.earned - a.earned);
  }, [earnedRows, paidSlips, campaignById]);

  /* ---------- Cash vs reward campaigns ----------
     A Travel campaign doesn't pay ringgit — it pays a trip ("Europe Trip (1 pax) or
     Cash RM5,000" at RM4.2m). Listing it in the money table put RM0.00 in every
     column, which reads as "earned nothing" when the agent may be most of the way
     to a trip. So reward campaigns get their own section showing the TIER and the
     REWARD, and the money table keeps only the campaigns that actually pay money.
     (user 2026-08-27) */
  const isRewardType = (t) => t === 'Travel';
  const cashCampaigns = useMemo(() => byCampaign.filter(c => !isRewardType(c.type)), [byCampaign]);
  const rewardCampaignIds = useMemo(
    () => campaigns.filter(c => isRewardType(c.type)).map(c => c.campaignId),
    [campaigns]);

  // Achievement per agent on the reward campaigns, from the campaign engine itself
  // rather than re-derived here — GetCampaignProgress returns only the caller (used
  // on My), GetCampaignLeaderboard returns everyone (All only).
  const [rewardStanding, setRewardStanding] = useState({});
  useEffect(() => {
    let cancelled = false;
    const wanted = rewardCampaignIds.filter(id => campaignFilter === 'all' || String(id) === String(campaignFilter));
    if (wanted.length === 0) { setRewardStanding({}); return; }
    Promise.all(wanted.map(id =>
      (allScope ? getCampaignLeaderboard(id) : getCampaignProgress(id))
        .then(r => [id, r.data])
        .catch(() => [id, null])
    )).then(pairs => {
      if (cancelled) return;
      const out = {};
      pairs.forEach(([id, data]) => {
        if (!data) return;
        // Normalise both shapes to one list of { agentId, name, achieved }.
        out[id] = allScope
          ? (Array.isArray(data) ? data : []).map(e => ({
              agentId: e.agentId, name: e.agentName || 'Unknown', achieved: e.grandTotal ?? 0,
            }))
          : [{ agentId: myId, name: 'You', achieved: data.grandTotal ?? 0 }];
      });
      setRewardStanding(out);
    });
    return () => { cancelled = true; };
  }, [rewardCampaignIds, allScope, myId, campaignFilter]);

  /* Tier reached is resolved from the achieved figure against the campaign's own
     tiers, NOT from the tier NAME the API returns — the names repeat, so a name
     alone can't say which reward was earned.

     Crucially, a campaign can hold SEVERAL INDEPENDENT LADDERS sharing one target
     axis. The tour campaign has two, interleaved:

       Rising Star Club (RSC)          1.0m  2.0m  3.0m   → China trips
       Multi-Million Elite Club (MMEC) 2.1m  4.2m  5.5m   → Europe trips

     Sorting all six by target and taking "the highest one reached" is wrong and
     silently loses a reward: Heng Wai Lee at RM3.69m has topped out RSC *and*
     cleared MMEC's first rung, but a single ladder shows only the RSC line. So
     the highest reached rung is resolved PER CLUB and every club the agent
     qualifies in is listed. Next tier is the nearest unreached rung anywhere.
     (user 2026-08-27) */
  const rewardSections = useMemo(() => {
    return campaigns
      .filter(c => isRewardType(c.type))
      .filter(c => campaignFilter === 'all' || String(c.campaignId) === String(campaignFilter))
      .map(c => {
        const tiers = [...(c.tiers || [])].sort((a, b) => (a.target ?? 0) - (b.target ?? 0));
        // Club = the tier name, trimmed — the data carries trailing spaces on some
        // rows, which would otherwise split one club into two.
        const clubs = [];
        tiers.forEach(t => {
          const club = String(t.tierName || '').trim();
          let g = clubs.find(x => x.club === club);
          if (!g) { g = { club, rungs: [] }; clubs.push(g); }
          g.rungs.push(t);
        });

        const resolve = (achieved) => {
          const reached = [];
          clubs.forEach(({ club, rungs }) => {
            let best = null;
            rungs.forEach(t => { if (achieved >= (t.target ?? 0)) best = t; });
            if (best) reached.push({ club, reward: String(best.reward || '').trim(), target: best.target });
          });
          const next = tiers.find(t => achieved < (t.target ?? 0)) || null;
          return { reached, next };
        };

        const rows = (rewardStanding[c.campaignId] || [])
          .filter(r => (r.achieved ?? 0) > 0)
          .map(r => ({ ...r, ...resolve(r.achieved ?? 0) }))
          .sort((a, b) => b.achieved - a.achieved);
        return { campaign: c, tiers, clubs, rows, qualified: rows.filter(r => r.reached.length > 0).length };
      });
  }, [campaigns, campaignFilter, rewardStanding]);

  /* ---------- By agent ---------- */
  const byAgent = useMemo(() => {
    const map = {};
    const touch = (id, name) => (map[id] ||= { agentId: id, name: name || 'Unknown', earned: 0, paid: 0, campaigns: new Set() });
    earnedRows.forEach(r => {
      if (!r.beneficiaryAgentId) return;
      const m = touch(r.beneficiaryAgentId, r.agentName);
      m.earned += r.amount ?? 0;
      if (r.campaignName) m.campaigns.add(r.campaignName);
    });
    paidSlips.forEach(s => { if (s.agentId) touch(s.agentId, s.agentName).paid += s.totalPayout ?? 0; });
    return Object.values(map)
      .map(a => ({ ...a, campaigns: a.campaigns.size, pending: Math.max(0, a.earned - a.paid) }))
      .filter(a => a.earned > 0 || a.paid > 0)
      .sort((a, b) => b.earned - a.earned);
  }, [earnedRows, paidSlips]);

  const chartData = useMemo(() => cashCampaigns.slice(0, 10).map(c => ({
    name: c.name.length > 38 ? c.name.slice(0, 37) + '…' : c.name,
    Paid: Math.round(c.paid), Pending: Math.round(c.pending),
  })), [cashCampaigns]);

  const th = { fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '2px solid #E2E8F0' };
  const td = { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center', fontFamily: FONT, color: '#64748B' }}>Loading incentive data…</Box>;
  }

  return (
    <div style={{ padding: '20px 24px', background: '#F8FAFC', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>
            {allScope ? 'All Incentive Report' : 'My Incentive Report'}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748B', fontFamily: FONT }}>
            {allScope ? 'Every agent · earned from claims, paid from slips' : 'Your incentive · earned from claims, paid from slips'}
          </p>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 230 }}>
            <Select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}
              sx={{ borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' }}>
              <MenuItem value="all" sx={{ fontFamily: FONT, fontSize: '0.85rem' }}>All campaigns</MenuItem>
              {campaigns.map(c => (
                <MenuItem key={c.campaignId} value={c.campaignId} sx={{ fontFamily: FONT, fontSize: '0.85rem' }}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
            sx={{ backgroundColor: '#10B981', textTransform: 'none', fontFamily: FONT, fontSize: '0.82rem', borderRadius: '8px', '&:hover': { backgroundColor: '#059669' } }}>
            Print / Save as PDF
          </Button>
        </Box>
      </Box>

      {/* Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <StatCard label="Total Incentive" value={handlePriceWithRM(Math.round(cashEarned))}
          sub={allScope ? `${byAgent.length} agent${byAgent.length === 1 ? '' : 's'} · earned` : 'Earned across all campaigns'} borderColor="#1A8CFF" />
        <StatCard label="Paid" value={handlePriceWithRM(Math.round(totalPaid))}
          sub={`${paidSlips.length} approved slip${paidSlips.length === 1 ? '' : 's'}`} borderColor="#10B981" />
        <StatCard label="Pending" value={handlePriceWithRM(Math.round(pending))}
          sub={cashEarned > 0 ? `${Math.round(pending / cashEarned * 100)}% unpaid` : 'Nothing earned'} borderColor="#F59E0B" />
        <StatCard label="Campaigns" value={byCampaign.length}
          sub={`${activeCampaigns} active${tripAwards ? ` · ${tripAwards} trip reward${tripAwards === 1 ? '' : 's'}` : ''}`} borderColor="#6366F1" />
      </Box>

      {/* Paid vs Pending by campaign */}
      <Card sx={{ borderRadius: '14px', mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>Paid vs Pending by Campaign</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
            Paid = approved slips (money to the agent) · Pending = earned but not yet slipped
          </Typography>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(240, chartData.length * 42)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 40, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#EEF2F6" />
                <XAxis type="number" tick={{ fontSize: 11, fontFamily: FONT, fill: '#64748B' }} />
                <YAxis type="category" dataKey="name" width={250} tick={{ fontSize: 11, fontFamily: FONT, fill: '#334155' }} />
                <Tooltip formatter={(v) => handlePriceWithRM(v)} contentStyle={{ fontFamily: FONT, fontSize: '0.78rem', borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontFamily: FONT, fontSize: '0.75rem' }} />
                <Bar dataKey="Paid" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Pending" stackId="a" fill="#F59E0B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4, fontFamily: FONT }}>No incentive data</Typography>}
        </CardContent>
      </Card>

      {/* By campaign */}
      <Card sx={{ borderRadius: '14px', mb: 2 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>Cash Incentive by Campaign</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
            {cashCampaigns.length} campaigns that pay ringgit · trip and item rewards are listed separately below
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={th}>Campaign</TableCell>
                  <TableCell sx={th}>Type</TableCell>
                  {/* The achievement the money is calculated from — units on an Item
                      campaign, cases on a Case campaign. (user 2026-08-27) */}
                  <TableCell sx={th} align="right">Achieved</TableCell>
                  {/* Company-wide only — on the personal report every row says "1",
                      which is noise. (user 2026-08-27) */}
                  {allScope && <TableCell sx={th} align="right">Agents</TableCell>}
                  <TableCell sx={th} align="right">Earned</TableCell>
                  <TableCell sx={th} align="right">Paid</TableCell>
                  <TableCell sx={th} align="right">Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cashCampaigns.map(c => (
                  <TableRow key={c.campaignId} hover>
                    <TableCell sx={{ ...td, fontWeight: 500 }}>{c.name}</TableCell>
                    <TableCell sx={td}>
                      <Chip size="small" label={c.metric === 'Addon' ? 'Battery' : c.metric === 'Cases' ? 'Cases' : c.metric || '—'}
                        sx={{ fontFamily: FONT, fontSize: '0.68rem', height: 20 }} />
                    </TableCell>
                    <TableCell sx={td} align="right">
                      {c.units > 0
                        ? `${Number(c.units.toFixed(1))} ${c.metric === 'Addon' ? (c.units === 1 ? 'unit' : 'units') : (c.units === 1 ? 'case' : 'cases')}`
                        : '—'}
                    </TableCell>
                    {allScope && <TableCell sx={td} align="right">{c.agents}</TableCell>}
                    <TableCell sx={{ ...td, fontWeight: 700 }} align="right">{handlePriceWithRM(Math.round(c.earned))}</TableCell>
                    <TableCell sx={{ ...td, color: '#10B981' }} align="right">{handlePriceWithRM(Math.round(c.paid))}</TableCell>
                    <TableCell sx={{ ...td, color: '#F59E0B' }} align="right">{handlePriceWithRM(Math.round(c.pending))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ===== Reward campaigns — trips and items, not ringgit =====
          One card per campaign. The point of these is WHICH REWARD was reached, so
          the tier and its reward text carry the row and there is no money column to
          read as zero. Agents below the first tier still appear with what they need
          for it — being 40% of the way to a Europe trip is information, and the old
          "RM0.00" hid it. */}
      {rewardSections.map(({ campaign, tiers, clubs, rows, qualified }) => (
        <Card key={campaign.campaignId} sx={{ borderRadius: '14px', mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', fontFamily: FONT }}>
                {String(campaign.name || '').trim()}
              </Typography>
              <Chip size="small" label={campaign.type}
                sx={{ fontFamily: FONT, fontSize: '0.68rem', height: 20, backgroundColor: '#EDE9FE', color: '#5B21B6' }} />
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
              Reward campaign · {qualified} of {rows.length} qualified
              {clubs.length > 1 && ` · ${clubs.length} independent clubs, an agent can hold more than one`}
              {tiers.length > 0 && ` · entry at ${handlePriceWithRM(tiers[0].target)}`}
            </Typography>
            {rows.length > 0 ? (
              <TableContainer sx={{ maxHeight: 420, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {allScope && <TableCell sx={{ ...th, backgroundColor: '#FFF' }}>Agent</TableCell>}
                      <TableCell sx={{ ...th, backgroundColor: '#FFF' }} align="right">Achieved</TableCell>
                      <TableCell sx={{ ...th, backgroundColor: '#FFF' }}>Tier Reached</TableCell>
                      <TableCell sx={{ ...th, backgroundColor: '#FFF' }}>Reward Earned</TableCell>
                      <TableCell sx={{ ...th, backgroundColor: '#FFF' }}>Next Tier</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.agentId} hover>
                        {allScope && <TableCell sx={{ ...td, fontWeight: 500 }}>{r.name}</TableCell>}
                        <TableCell sx={{ ...td, fontWeight: 700 }} align="right">{handlePriceWithRM(Math.round(r.achieved))}</TableCell>
                        {/* One line per club the agent qualifies in — the two ladders
                            are independent, so an agent can hold both at once. */}
                        <TableCell sx={td}>
                          {r.reached.length > 0
                            ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                {r.reached.map(x => (
                                  <Chip key={x.club} size="small" label={x.club}
                                    sx={{ fontFamily: FONT, fontSize: '0.68rem', height: 20, backgroundColor: '#DCFCE7', color: '#166534' }} />
                                ))}
                              </Box>
                            : <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: FONT }}>Not yet qualified</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...td, fontWeight: r.reached.length ? 600 : 400, color: r.reached.length ? '#0F172A' : '#94A3B8' }}>
                          {r.reached.length > 0
                            ? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {r.reached.map(x => <span key={x.club}>{x.reward}</span>)}
                              </Box>
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ ...td, color: '#64748B' }}>
                          {r.next
                            ? `${String(r.next.tierName || '').trim()} · ${handlePriceWithRM(Math.round((r.next.target ?? 0) - r.achieved))} to go`
                            : 'Top tier reached'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 3, fontFamily: FONT }}>
                No progress on this campaign yet
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}

      {/* By agent — company-wide page ONLY. On My Incentive this table would be a list
          of one, and on a broken route it would be everyone else's earnings. */}
      {allScope && (
      <Card sx={{ borderRadius: '14px', mb: 4 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>By Agent</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
            {byAgent.length} agents · sorted by incentive earned
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={th}>#</TableCell>
                  <TableCell sx={th}>Agent</TableCell>
                  <TableCell sx={th} align="right">Campaigns</TableCell>
                  <TableCell sx={th} align="right">Earned</TableCell>
                  <TableCell sx={th} align="right">Paid</TableCell>
                  <TableCell sx={th} align="right">Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byAgent.map((a, i) => (
                  <TableRow key={a.agentId} hover>
                    <TableCell sx={{ ...td, color: '#64748B', fontWeight: 600 }}>{i + 1}</TableCell>
                    <TableCell sx={{ ...td, fontWeight: 500 }}>{a.name}</TableCell>
                    <TableCell sx={td} align="right">{a.campaigns}</TableCell>
                    <TableCell sx={{ ...td, fontWeight: 700 }} align="right">{handlePriceWithRM(Math.round(a.earned))}</TableCell>
                    <TableCell sx={{ ...td, color: '#10B981' }} align="right">{handlePriceWithRM(Math.round(a.paid))}</TableCell>
                    <TableCell sx={{ ...td, color: '#F59E0B' }} align="right">{handlePriceWithRM(Math.round(a.pending))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
